import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Clock3,
  KeyRound,
  LockKeyhole,
  Moon,
  ScrollText,
  ShieldCheck,
  Sun,
} from "lucide-react";
import toast from "react-hot-toast";
import { authService } from "@/services/auth.service";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { FormField } from "@/components/FormField";
import { useAuth } from "@/hooks/useAuth";
import { Topbar } from "@/components/Topbar";
import { useTheme } from "@/theme/ThemeProvider";

export default function AccountSettings() {
  const { logout } = useAuth();
  const { mode, setMode } = useTheme();
  const nav = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function save(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError("");
    if (newPassword !== confirmation) {
      setError("The new passwords do not match.");
      return;
    }
    if (
      newPassword.length < 12 ||
      new TextEncoder().encode(newPassword).length > 72
    ) {
      setError("Use at least 12 characters and no more than 72 bytes.");
      return;
    }
    setBusy(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      logout();
      toast.success("Password changed. Sign in with your new password.");
      nav("/login", { replace: true });
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          "Unable to change your password. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Topbar title="Account security" breadcrumb="Account / Security" />
      <div className="admin-page space-y-5">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">
            Your account, protected
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Manage your password and personalize your workspace.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            {
              icon: ShieldCheck,
              title: "Administrator access",
              detail: "Platform-wide company management",
              tone: "bg-success-soft text-success",
            },
            {
              icon: Clock3,
              title: "One-hour sessions",
              detail: "Sign in again after your session expires",
              tone: "bg-info-soft text-info",
            },
            {
              icon: KeyRound,
              title: "Password sign-in",
              detail: "Protected against repeated login attempts",
              tone: "bg-primary-soft text-primary",
            },
          ].map((item) => (
            <div
              key={item.title}
              className="admin-panel flex items-start gap-3 p-4"
            >
              <span className={`rounded-xl p-2.5 ${item.tone}`}>
                <item.icon size={18} />
              </span>
              <div>
                <h3 className="text-xs font-semibold">{item.title}</h3>
                <p className="mt-1 text-[11px] leading-relaxed text-text-muted">
                  {item.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="grid items-start gap-4 lg:grid-cols-5">
          <section className="admin-panel p-5 sm:p-6 lg:col-span-3">
            <div className="mb-6 flex items-start gap-3">
              <span className="rounded-lg bg-primary-soft p-2 text-primary">
                <LockKeyhole size={20} />
              </span>
              <div>
                <h2 className="text-base font-semibold text-text-primary">
                  Change password
                </h2>
                <p className="mt-1 text-xs leading-relaxed text-text-secondary">
                  Use a unique password to keep platform access secure.
                </p>
              </div>
            </div>
            <form onSubmit={save}>
              <FormField label="Current password" htmlFor="current-password">
                <Input
                  id="current-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </FormField>
              <FormField
                label="New password"
                htmlFor="new-password"
                hint="At least 12 characters"
              >
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  maxLength={72}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </FormField>
              <FormField
                label="Confirm new password"
                htmlFor="confirm-password"
              >
                <Input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                />
              </FormField>
              {error && (
                <p role="alert" className="mb-4 text-sm text-danger">
                  {error}
                </p>
              )}
              <div className="mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
                <p className="max-w-[230px] text-xs leading-relaxed text-text-muted">
                  Updating your password signs out every admin session.
                </p>
                <Button
                  type="submit"
                  loading={busy}
                  iconLeft={<LockKeyhole size={14} />}
                >
                  Update password
                </Button>
              </div>
            </form>
          </section>
          <div className="space-y-4 lg:col-span-2">
            <section className="admin-panel p-5">
              <h2 className="text-sm font-semibold">Workspace appearance</h2>
              <p className="mt-1 text-xs text-text-muted">
                Choose the theme that works best for you.
              </p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {(["light", "dark"] as const).map((theme) => (
                  <button
                    type="button"
                    key={theme}
                    aria-pressed={mode === theme}
                    onClick={() => setMode(theme)}
                    className={`rounded-xl border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${mode === theme ? "border-primary bg-primary-soft" : "border-border hover:border-primary/50"}`}
                  >
                    <div
                      aria-hidden
                      className={`mb-3 flex h-16 gap-1.5 rounded-lg border p-2 ${theme === "light" ? "border-[#e8e5dc] bg-[#fafaf7]" : "border-[#303036] bg-[#131316]"}`}
                    >
                      <div className="w-1/4 rounded bg-primary/30" />
                      <div className="flex flex-1 flex-col gap-1.5">
                        <div className="h-2 rounded bg-primary/50" />
                        <div
                          className={`flex-1 rounded ${theme === "light" ? "bg-[#eeece5]" : "bg-[#26262c]"}`}
                        />
                      </div>
                    </div>
                    <span className="flex items-center gap-2 text-xs font-medium">
                      {theme === "light" ? (
                        <Sun size={14} className="text-warning" />
                      ) : (
                        <Moon size={14} className="text-info" />
                      )}
                      <span className="capitalize">{theme}</span>
                      {mode === theme && (
                        <Check size={14} className="ml-auto text-primary" />
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </section>
            <section className="admin-panel p-5">
              <div className="flex items-center gap-2">
                <ShieldCheck size={17} className="text-success" />
                <h2 className="text-sm font-semibold">Password checklist</h2>
              </div>
              <ul className="mt-4 space-y-3 text-xs text-text-secondary">
                {[
                  {
                    text: "At least 12 characters",
                    met: newPassword.length >= 12,
                  },
                  {
                    text: "Different from your current password",
                    met:
                      !!currentPassword &&
                      !!newPassword &&
                      currentPassword !== newPassword,
                  },
                  {
                    text: "New passwords match",
                    met: !!newPassword && newPassword === confirmation,
                  },
                ].map((item) => (
                  <li key={item.text} className="flex items-center gap-2">
                    <Check
                      size={14}
                      className={
                        item.met ? "text-success" : "text-text-muted/50"
                      }
                    />
                    <span>
                      {item.text}
                      {item.met && <span className="sr-only">: complete</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
            <Link
              to="/audit-log"
              className="admin-panel group flex items-center gap-3 p-4 transition-colors hover:border-primary/40"
            >
              <span className="rounded-lg bg-info-soft p-2 text-info">
                <ScrollText size={18} />
              </span>
              <span className="flex-1">
                <span className="block text-xs font-semibold">
                  Review platform activity
                </span>
                <span className="mt-1 block text-[11px] text-text-muted">
                  Sign-ins and administrative changes
                </span>
              </span>
              <ArrowRight
                size={15}
                className="text-text-muted group-hover:text-primary"
              />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
